import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router-dom"

import {
  UButton,
  UFileBox,
  UFileProcessingCard,
  UModalPopup,
  UText,
} from "../../../components"
import NavigationBlockPopup from "../../../components/NavigationBlockPopup/NavigationBlockPopup"
import Preview from "../../../components/Preview/preview2"
import { useAppDispatch, useAppSelector } from "../../../core/app/hooks"
import { setClinicalData } from "../../../core/app/slices/clinical/clinicalSlice"
import { setUploadStls } from "../../../core/app/slices/records/scans/scansSlice"
import { RootState } from "../../../core/app/store"
import { useCallbackPrompt } from "../../../hooks/useCallbackPrompt"
import { ROUTE_ATTENTION_BANNER } from "../../../routes/status.route.config"
import { Box, Grid } from "../../../ui-component/mui.components"
import { preview, resourcesSynchronization } from "@/gluelayer"

import {
  file,
  fileBox,
  fileBoxContent,
  previewBox,
  stltitle,
  title,
  zone,
} from "./Scans.style"
import useLocalStorageListener from "@/modules/Settings/Integrations/localStorageHooks"
import { useScannerAccess } from "@/hooks/feature-flags/useScannerAccess"
import ScannerPatients from "@/modules/Patient/ScannerPatients/ScannerPatients"
import { setData } from "@/core/app/slices/patients/patientSlice"
import { getCaseDetail } from "@/core/global/ThirdDataMgr"
import { downloadSTL, getCaseByUuid, getFileUrl } from "@/core/global/httpMedit"
import { downloadScan } from "@/core/global/http3Shape"
import {
  downloadAlliedStarFilesbyName,
} from "@/core/global/httpAlliedstar"
import { convertMBtoKB } from "@/core/utils/formatters"
import { getScannerConnectionStatus } from "@/core/app/slices/integrations"
import { SCANNER_INTEGRATION_IDS } from "@/core/app/slices/integrations/scannerConstants"
import { useEasyRxScansImport } from "./useEasyRxScansImport"
import { updateCaseById } from "@/core/app/slices/case"
/**
 * a component for upload stl & show the teeth model
 * @param downLoadZips: 下载stl文件
 * @param uploadScans: 上传stl文件
 * @param isClickNext: click the next button
 * @returns scans compoent
 */

interface scansType {
  isRefinement: boolean
  handleLowerStl?: any
  handleUpperStl?: any
  handleShowStl?: any
  caseType: "new_scan" | "hold_scan" | "hold_refinement"
}

interface ScansHandle {
  // Define any methods you want to expose to the parent component
  saveRefinementSTLtoBulk10Zip: () => boolean
}

interface ScansProps {
  isRefinement?: boolean
  handleLowerStl?: (action: string) => void
  handleUpperStl?: (action: string) => void
  handleShowStl?: (showStl: { isupper: boolean; islower: boolean }) => void
}
/**
 * Using this Scans component for Refienement page
 */
const Scans: React.ForwardRefRenderFunction<ScansHandle, ScansProps> = (
  { isRefinement, handleLowerStl, handleUpperStl, handleShowStl },
  ref,
) => {
  const MAX_ALLOWED_FILE_SIZE_MB = import.meta.env.VITE_ALLOWED_STL_FILE_SIZE_MB

  const { t } = useTranslation("common")

  const { patientId, caseId } = useParams()

  const [upperStl, setUpperStl] = useState({ data: null, action: "init" })
  const [lowerStl, setLowerStl] = useState({ data: null, action: "init" })
  const [holePopup, setholePopup] = useState(false)
  const [nextPopup, setnextPopup] = useState(false)
  const [showStl, setShowStl] = useState({ isupper: false, islower: false })

  const { isRouteChangeBlocked } = useAppSelector(
    (state: RootState) => state.navigationPopup,
  )

  const { uploadSTL } = useAppSelector((state) => state.scansService)
  const { zipList } = useAppSelector((state) => state.clinicalService)
  const { isCreateSelfplanRefinement } = useAppSelector((state: RootState) => state.udTreatService)

  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(isRouteChangeBlocked)

  const [isShowBothScansPopup, setIsShowBothScansPopup] =
    useState<boolean>(true)
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { selectedScannerPatient } = useAppSelector(
    (state: RootState) => state.integrationsService,
  )
  const dispatch = useAppDispatch()
  const location = useLocation()

  const gotoNextStep = () => {
    dispatch(setClinicalData({ isLoading: "pending" }))
  }

  const checkLowerFileUpload = (file) => {
    handleLowerStl && handleLowerStl(file.action)
  }

  const checkUpperFileUpload = (file) => {
    handleUpperStl && handleUpperStl(file.action)
  }

  const { importEasyRxScans } = useEasyRxScansImport({
    onUpperStl: (file) => {
      const stl = { data: file, action: file ? "stl" : "delete" }
      setUpperStl(stl)
      checkUpperFileUpload(stl)
      dispatch(setUploadStls({ upper: file }))
    },
    onLowerStl: (file) => {
      const stl = { data: file, action: file ? "stl" : "delete" }
      setLowerStl(stl)
      checkLowerFileUpload(stl)
      dispatch(setUploadStls({ lower: file }))
    },
  })

  useEffect(() => {
    ;(async () => {
      // 1. get the jsondata from zip10/refinement/arch_data.json
      // 2. read the course_check_number in the jsondata
      // 3. if the jsondata is null or course_check_number!==uassist_round+1, not need to  reload the mtc

      const arch_data = resourcesSynchronization.getFileDirectly(
        "refinement/arch_data.json",
        "Setting Data10",
        "utf8",
      )
      console.log("🚀 ~ Scans2 ~ arch_data:", arch_data)
      if (!arch_data) return //if the josnfile is not exist, means this is the first time to submit the refinement in Ucloud

      const arch_data_json = JSON.parse(arch_data)
      const course_check_number = arch_data_json?.course_check_number ?? 0

      const uassist_round = caseDetail?.uassist_round ?? 0
      console.log("🚀 ~ Scans2 ~ uassist_round:", uassist_round)
      if (course_check_number !== uassist_round + 1) return // not the latest refinement, no need to load the mtc

      // load the mtc from zipList

      const mtcNames = [
        "/test/case/Setting Data10/refinement/arch_u.mtc",
        "/test/case/Setting Data10/refinement/arch_l.mtc",
      ]
      // await resourcesSynchronization.writeCaseFilesToVirtualFS(zipList)  // not need to write the ziplist to vfs again, because it has been written when open the case

      const res = resourcesSynchronization.getFilesByPathname(mtcNames)

      if (!res || res.size === 0) {
        console.log("no refinement stl found in bulk10 zip")
        return
      }
      console.log("🚀 ~ preview.getMtcFromZips ~ res:", res)
      const upperModel = res.get("arch_u.mtc")
      const lowerModel = res.get("arch_l.mtc")

      setUpperStl &&
        upperModel &&
        setUpperStl({
          data: upperModel || null,
          action: "download",
        })

      setLowerStl &&
        lowerModel &&
        setLowerStl({
          data: lowerModel || null,
          action: "download",
        })
      dispatch(
        setUploadStls({
          upper: upperModel,
          lower: lowerModel,
        }),
      )
    })()
  }, [zipList])

  useEffect(() => {
    console.log("component scans value:", uploadSTL)
    if (uploadSTL.upper) {
      setUpperStl({
        data: uploadSTL.upper,
        action: "stl",
      })

      checkUpperFileUpload({ data: uploadSTL.upper, action: "stl" })
    } else {
      setUpperStl({
        data: null,
        action: "delete",
      })
      checkUpperFileUpload({ data: null, action: "delete" })
    }

    if (uploadSTL.lower) {
      setLowerStl({
        data: uploadSTL.lower,
        action: "stl",
      })
      checkLowerFileUpload({ data: uploadSTL.lower, action: "stl" })
    } else {
      setLowerStl({
        data: null,
        action: "delete",
      })
      checkLowerFileUpload({ data: null, action: "delete" })
    }
  }, [uploadSTL])

  useEffect(() => {
    if (caseDetail) {
      const udesignCategory = caseDetail?.udesign_category
      if (udesignCategory) {
        const arches = udesignCategory.split("/")

        // It was used to determine whether there was an upper or lower jaw
        setShowStl({
          isupper: arches[0].includes("R") || arches[0].includes("A"),
          islower: arches[1].includes("R") || arches[1].includes("A"),
        })
      }
    }
  }, [caseDetail])

  useEffect(() => {
    handleLowerStl && handleShowStl(showStl)
  }, [showStl])

  useImperativeHandle(ref, () => ({
    saveRefinementSTLtoBulk10Zip: () => {
      return !!preview.saveStlToMtc(upperStl.data, lowerStl.data, true)
    },
  }))

  //scanner
  const { caseDetailForShape, scannerSelect } = useAppSelector(
    (state: RootState) => state.patientService,
  )
  console.log("caseDetailForShape", caseDetailForShape)
  const isScannerEnabled = useScannerAccess()
  
  const { connectionStatus } = useAppSelector(
      (state: RootState) => state.integrationsService,
    )
  useEffect(() => {
    dispatch(getScannerConnectionStatus())
  }, [dispatch])

  // locastorage key  3shapeData， meditData, alliesData
  const shapeLocal = useLocalStorageListener("3shapeData", null)
  const meditDataLocal = useLocalStorageListener("meditData", null)
  const alliesDataLocal = useLocalStorageListener("alliesData", null)
  const [scannerListData, setScannerListData] = useState([])
  // gei scanner list
  useEffect(() => {
    const tmp = []
    if (shapeLocal) {
      tmp.push({
        name: "3Shape",
        key: "3Shape",
      })
    }
    if (meditDataLocal) {
      tmp.push({
        name: "Medit",
        key: "Medit",
      })
    }
    if (alliesDataLocal) {
      tmp.push({
        name: "Alliedstar",
        key: "Alliedstar",
      })
    }
    setScannerListData(tmp)
  }, [shapeLocal, meditDataLocal, alliesDataLocal])
  // scanner patients box
  const [showScannerPatients, setShowScannerPatients] = useState<boolean>(false)
  const choosePatient = (row) => {
    if (scannerSelect === "3Shape") {
      // stl and image
      const { Scans = [], Attachments = [], Info } = row
      const obj = {}
      Scans.map((i) => {
        if (i.FileType === "stl" && i.Href) {
          if (i.JawType === "upper") {
            const pre = i.Href.split("attachments")[0]
            obj.upper = `${pre}attachments/${i.Hash}`
          }
          if (i.JawType === "lower") {
            const pre = i.Href.split("attachments")[0]
            obj.lower = `${pre}attachments/${i.Hash}`
          }
        }
      })
      const imageTmp = []
      Attachments.map((i) => {
        if (["png", "jpg", "jpeg"].includes(i.FileType) && i.Href) {
          const obj = {}
          const pre = i.Href.split("attachments")[0]
          obj.href = `${pre}attachments/${i.Hash}`
          obj.name = i.Name
          obj.fileType = `image/${i.FileType}`
          imageTmp.push(obj)
        }
      })
      obj.Info = Info
      obj.Scans = Scans
      obj.Attachments = imageTmp
      dispatch(setData({ caseDetailForShape: obj }))
    }
    if (scannerSelect === "Alliedstar") {
      const detail = JSON.parse(JSON.stringify(row))
      getCaseDetail({ type: "Alliedstar", id: row.Info.CaseId }).then((res) => {
        if (res && res.attachments) {
          const scanTmp = []
          const imageTmp = []
          res.attachments.map((i) => {
            if (
              i.fileFormat === "stl" &&
              (i.fileName.includes("upper") || i.fileName.includes("lower"))
            ) {
              scanTmp.push(i)
            } else if (i.fileFormat !== "stl") {
              imageTmp.push(i)
            }
          })
          detail.Attachments = imageTmp
          detail.Scans = scanTmp
        }
        dispatch(setData({ caseDetailForShape: detail }))
      })
    }
    if (scannerSelect === "Medit") {
      const detail = JSON.parse(JSON.stringify(row))
      getCaseByUuid(row.Info.CaseId).then((res) => {
        if (res && res.data.files) {
          const scanTmp = []
          const imageTmp = []
          const keywords = ["mandibular", "maxillary", "arch"]
          res.data.files.map((i) => {
            if (keywords.some((keyword) => i.name.includes(keyword))) {
              scanTmp.push(i)
            } else {
              imageTmp.push(i)
            }
          })
          detail.Attachments = imageTmp
          detail.Scans = scanTmp
        }
        dispatch(setData({ caseDetailForShape: detail }))
      })
    }
  }

  // download stls and upload to server
  useEffect(() => {
    if (isScannerEnabled) {
      if (
        scannerSelect === "3Shape" &&
        caseDetailForShape.Scans &&
        caseDetailForShape.Scans.length
      ) {
        if (caseDetailForShape.upper && !(isCreateSelfplanRefinement && !showStl.isupper)) {
          downloadScan(
            caseDetailForShape.upper,
            "upper.stl",
            null,
            dispatch,
          ).then((res) => {
            if (!res) return
            dispatch(
              setUploadStls({
                upper: res,
              }),
            )
            setUpperStl({ data: res, action: "stl" })
            checkUpperFileUpload({ data: res, action: "stl" })
          })
        }
        if (caseDetailForShape.lower && !(isCreateSelfplanRefinement && !showStl.islower)) {
          downloadScan(
            caseDetailForShape.lower,
            "lower.stl",
            null,
            dispatch,
          ).then((res) => {
            if (!res) return
            dispatch(
              setUploadStls({
                lower: res,
              }),
            )
            setLowerStl({ data: res, action: "stl" })
            checkLowerFileUpload({ data: res, action: "stl" })
          })
        }
      } else if (
        scannerSelect === "Alliedstar" &&
        caseDetailForShape.Scans &&
        caseDetailForShape.Scans.length
      ) {
        downloadAlliedStarFilesbyName(
          caseDetailForShape.Info.CaseId,
          ["upper.stl", "lower.stl"],
          dispatch,
        ).then((res) => {
          if (res && res.upper && !(isCreateSelfplanRefinement && !showStl.isupper)) {
            dispatch(
              setUploadStls({
                upper: res.upper,
              }),
            )
            setUpperStl({ data: res.upper, action: "stl" })
            checkUpperFileUpload({ data: res.upper, action: "stl" })
          }
          if (res && res.lower && !(isCreateSelfplanRefinement && !showStl.islower)) {
            dispatch(
              setUploadStls({
                lower: res.lower,
              }),
            )
            setLowerStl({ data: res.lower, action: "stl" })
            checkLowerFileUpload({ data: res.lower, action: "stl" })
          }
        })
        // downloadAlliedStarFilesbyFormat(
        //   caseDetailForShape.Info.CaseId,
        //   ["stl"],
        //   dispatch,
        // ).then((res) => {
        //   if (res && res.upper) {
        //     dispatch(
        //       setUploadStls({
        //         upper: res.upper,
        //       }),
        //     )
        //     setUpperStl({ data: res.upper, action: "stl" })
        //     checkUpperFileUpload({ data: res.upper, action: "stl" })
        //   }
        //   if (res && res.lower) {
        //     dispatch(
        //       setUploadStls({
        //         lower: res.lower,
        //       }),
        //     )
        //     setLowerStl({ data: res.lower, action: "stl" })
        //     checkLowerFileUpload({ data: res.lower, action: "stl" })
        //   }
        // })
      } else if (
        scannerSelect === "Medit" &&
        caseDetailForShape.Scans &&
        caseDetailForShape.Scans.length
      ) {
        //
        console.log("🚀 caseDetailForShape:", caseDetailForShape)
        const upkeywords = ["maxillary", "arch_u"]
        const lowkeywords = ["mandibular", "arch_l"]
        let hasUpper = false,
          haLower = false
        caseDetailForShape.Scans.forEach((scan) => {
          if (
            upkeywords.some((keyword) => scan.name.includes(keyword)) &&
            !hasUpper
          ) {
            hasUpper = true
            if (isCreateSelfplanRefinement && !showStl.isupper) return
            getFileUrl(scan.uuid).then((res) => {
              if (res) {
                downloadSTL(res, "upper.stl", dispatch).then((file) => {
                  if (file) {
                    dispatch(
                      setUploadStls({
                        upper: file,
                      }),
                    )
                    setUpperStl({ data: file, action: "stl" })
                    checkUpperFileUpload({ data: file, action: "stl" })
                  }
                })
              }
            })
          }
          if (
            lowkeywords.some((keyword) => scan.name.includes(keyword)) &&
            !haLower
          ) {
            haLower = true
            if (isCreateSelfplanRefinement && !showStl.islower) return
            getFileUrl(scan.uuid).then((res) => {
              if (res) {
                downloadSTL(res, "lower.stl", dispatch).then((file) => {
                  if (file) {
                    dispatch(
                      setUploadStls({
                        lower: file,
                      }),
                    )
                    setLowerStl({ data: file, action: "stl" })
                    checkLowerFileUpload({ data: file, action: "stl" })
                  }
                })
              }
            })
          }
        })
      }
    }
  }, [caseDetailForShape])

  return (
    <Box id="scansroot" sx={{ width: "100%" }}>
      <Grid>
        <UModalPopup
          sxModalProps={{
            ".MuiDialog-paper": {
              maxWidth: 444,
            },
            "#titleCntr": {
              padding: "16px 24px",
            },
            "#contentCntr": {
              padding: "20px 24px",
              height: 192,
            },
            "#btnListCntr": {
              padding: 1,
              gap: 1,
            },
          }}
          title={
            <UText variant={"h6"}>{t("records.scans.holePopupTile")}</UText>
          }
          content={
            <Box>
              <span>{t("records.scans.holePopup1")}</span>
              <br />
              <br />
              <span>{t("records.scans.holePopup2")}</span>
            </Box>
          }
          isAlert={holePopup}
          btnList={[
            <UButton
              variant={"contained"}
              btnText={t("button.ok")}
              sxProp={{ width: 53, height: 36 }}
              key={"hole"}
              onClickHandler={() => setholePopup(false)}
            />,
          ]}
        />
        <UModalPopup
          sxModalProps={{
            ".MuiDialog-paper": {
              maxWidth: 444,
            },
            "#titleCntr": {
              padding: "16px 24px",
            },
            "#contentCntr": {
              padding: "20px 24px",
              // height: 182,
            },
            "#btnListCntr": {
              padding: 1,
              gap: 1,
            },
          }}
          title={
            <UText variant={"h6"}>{t("records.scans.nextPopupTile")}</UText>
          }
          content={
            <>
              <Grid container flexDirection={"column"}>
                <UText variant={"body1"}>
                  {t("records.scans.nextPopup1")}
                  <br />
                  <br />
                  {t("records.scans.nextPopup2")}
                </UText>
              </Grid>
              {/* <Grid sx={{ height: 38 }}>
              <Checkbox
                onChange={({ target: { checked } }) => {
                  if (checked) {
                    dispatch(
                      updateUserPreferences({
                        on_show_both_scan_upload_warning: true,
                      }),
                    )
                  } else {
                    dispatch(
                      updateUserPreferences({
                        on_show_both_scan_upload_warning: false,
                      }),
                    )
                  }
                }}
              />
              <UText variant={"body1"}>{t("popup.showAgain")}</UText>
            </Grid> */}
            </>
          }
          isAlert={
            (isShowBothScansPopup && nextPopup && !isRefinement) ||
            (isShowBothScansPopup &&
              nextPopup &&
              isRefinement &&
              showStl.islower &&
              showStl.isupper)
          }
          btnList={[
            <UButton
              variant={"contained"}
              btnText={"YES"}
              key={"yes"}
              sxProp={{ minWidth: 58, height: 36 }}
              onClickHandler={gotoNextStep}
            />,
            <UButton
              variant={"shade"}
              btnText={"NO"}
              key={"no"}
              onClickHandler={() => {
                setnextPopup(false)
              }}
              sxProp={{ minWidth: 54, height: 36 }}
            />,
          ]}
        />
        <UText sxProp={title} variant={"h5"}>
          {t("records.scans.title")}
        </UText>
        {isRefinement && (
          <UText
            variant="caption"
            sxProp={{
              display: "flex",
              justifyContent: "center",
              color: "#616161",
            }}
          >
            {t("records.refinement.description")}
          </UText>
        )}
        <Grid
          id="functionroot"
          container
          sx={{ ...previewBox, flexWrap: "nowrap" }}
        >
          <Box style={fileBox}>
            {(isRefinement ||
              (location.pathname.includes(
                ROUTE_ATTENTION_BANNER.attentionBanner,
              ) &&
                showStl.isupper) ||
              (!isRefinement &&
                !location.pathname.includes(
                  ROUTE_ATTENTION_BANNER.attentionBanner,
                ))) && (
              <Grid sx={{ ...zone }}>
                <UText
                  sxProp={{
                    ...stltitle,
                  }}
                  variant={"h6"}
                >
                  {"Upper"}
                </UText>
                {upperStl.action === "stl" || upperStl.action === "download" ? (
                  <UFileProcessingCard
                    fileName={
                      upperStl.action === "stl"
                        ? upperStl.data?.name
                        : "Uploaded Scan"
                    }
                    fileSize={upperStl.data?.size}
                    progressValue={100}
                    progressstatus={"complete"}
                    onRemove={() => {
                      setUpperStl({ data: null, action: "delete" })
                      checkUpperFileUpload({ data: null, action: "delete" })
                      dispatch(
                        setUploadStls({
                          upper: null,
                        }),
                      )
                      //clear scanner detail
                      dispatch(setData({ caseDetailForShape: {} }))
                    }}
                    removable={true}
                  ></UFileProcessingCard>
                ) : (
                  <UFileBox
                    id={"Upper"}
                    allowedFileExtensions={[
                      "vnd.ms-pki.stl",
                      "stl",
                      "sla",
                      "mtc",
                    ]}
                    allowedfileSize={convertMBtoKB(MAX_ALLOWED_FILE_SIZE_MB)}
                    isDisabled={isCreateSelfplanRefinement && !showStl.isupper ? true : false}
                    selectedFile={(file) => {
                      dispatch(
                        setUploadStls({
                          upper: file[0],
                        }),
                      )
                      setUpperStl({ data: file[0], action: "stl" })

                      checkUpperFileUpload({ data: file[0], action: "stl" })
                      //clear scanner detail
                      dispatch(setData({ caseDetailForShape: {} }))
                    }}
                    fileAcceptType={
                      ".stl,.mtc,model/stl,application/sla,application/vnd.ms-pki.stl"
                    }
                    boxSize={file}
                    fileBoxContent={fileBoxContent}
                    isRequired={false}
                    messages={{
                      fileNote: t("records.scans.fileNote"),
                      uploadButton: t("records.scans.uploadButton"),
                      uploadButtonSuffix: t("records.scans.uploadButtonSuffix"),
                      invalidfileFormat: t("records.scans.invalidfileFormat"),
                      invalidFileSize: t("records.scans.invalidSTLFileSizeLimit", {
                        maxStlFileSizeLimit: MAX_ALLOWED_FILE_SIZE_MB,
                      })
                    }}
                    fileLimit={1}
                  ></UFileBox>
                )}
              </Grid>
            )}
            {(isRefinement ||
              (location.pathname.includes(
                ROUTE_ATTENTION_BANNER.attentionBanner,
              ) &&
                showStl.islower) ||
              (!isRefinement &&
                !location.pathname.includes(
                  ROUTE_ATTENTION_BANNER.attentionBanner,
                ))) && (
              <Grid sx={{ ...zone }}>
                <UText sxProp={{ ...stltitle }} variant={"h6"}>
                  {"Lower"}
                </UText>
                {lowerStl.action === "stl" || lowerStl.action === "download" ? (
                  <UFileProcessingCard
                    fileName={
                      lowerStl.action === "stl"
                        ? lowerStl.data?.name
                        : "Uploaded Scan"
                    }
                    fileSize={lowerStl.data?.size}
                    progressValue={100}
                    progressstatus={"complete"}
                    onRemove={() => {
                      setLowerStl({ data: null, action: "delete" })
                      checkLowerFileUpload({ data: null, action: "delete" })
                      dispatch(
                        setUploadStls({
                          lower: null,
                        }),
                      )
                      //clear scanner detail
                      dispatch(setData({ caseDetailForShape: {} }))
                    }}
                    removable={true}
                  ></UFileProcessingCard>
                ) : (
                  <UFileBox
                    id={"Lower"}
                    allowedFileExtensions={[
                      "vnd.ms-pki.stl",
                      "stl",
                      "sla",
                      "mtc",
                    ]}
                    allowedfileSize={convertMBtoKB(MAX_ALLOWED_FILE_SIZE_MB)}
                    isDisabled={isCreateSelfplanRefinement && !showStl.islower ? true : false}
                    selectedFile={(file) => {
                      // setScanErrors(false)
                      dispatch(
                        setUploadStls({
                          lower: file[0],
                        }),
                      )
                      setLowerStl({ data: file[0], action: "stl" })
                      checkLowerFileUpload({ data: file[0], action: "stl" })
                      //clear scanner detail
                      dispatch(setData({ caseDetailForShape: {} }))
                    }}
                    fileAcceptType={
                      ".stl,.mtc,model/stl,application/sla,application/vnd.ms-pki.stl"
                    }
                    boxSize={file}
                    fileBoxContent={fileBoxContent}
                    isRequired={false}
                    messages={{
                      fileNote: t("records.scans.fileNote"),
                      uploadButton: t("records.scans.uploadButton"),
                      uploadButtonSuffix: t("records.scans.uploadButtonSuffix"),
                      invalidfileFormat: t("records.scans.invalidfileFormat"),
                      invalidFileSize: t("records.scans.invalidSTLFileSizeLimit", {
                        maxStlFileSizeLimit: MAX_ALLOWED_FILE_SIZE_MB,
                      })
                    }}
                    fileLimit={1}
                  ></UFileBox>
                )}
              </Grid>
            )}
            {isScannerEnabled &&
              (scannerListData.length > 0 ||
                Object.values(connectionStatus).some(Boolean)) && (
                <UButton
                  sxProp={{ height: "36px", margin: "10px" }}
                  variant={"contained"}
                  btnType={"text"}
                  btnText={`Import Scans`}
                  onClickHandler={() => {
                    setShowScannerPatients(true)
                  }}
                />
              )
            }
          </Box>
          <Box
            id="3dcontainer"
            sx={{
              backgroundColor: "#333",
              position: "relative",
              width: "100%",
              margin: "8px",
              borderRadius: "10px",
            }}
          >
            {
              <Preview
                upperStl={upperStl}
                lowerStl={lowerStl}
                fillholeCallback={(isSuccess: boolean) => {
                  if (!isSuccess) {
                    setholePopup(true)
                  }
                }}
                placeHolder={t("records.scans.previewPlaceHolder")}
              />
            }
          </Box>
        </Grid>
      </Grid>
      <NavigationBlockPopup
        cancelNavigation={cancelNavigation}
        confirmNavigation={confirmNavigation}
        showPrompt={showPrompt}
      />

      <ScannerPatients
        showScannerPatients={showScannerPatients}
        setShowScannerPatients={setShowScannerPatients}
        handleScannerPatientDetailSelect={(rowInfo) => {
          const externalCaseId = rowInfo.CaseId
          setShowScannerPatients(false)
          dispatch(
            updateCaseById({
              patientId,
              caseId,
              payload: {
                "extra_info": JSON.stringify({
                  ...caseDetail?.extra_info,
                  scanner: { name: selectedScannerPatient?.scanner  , id: Number(externalCaseId) }
                })
              },
            }),
          ).then(()=>{
            importEasyRxScans(externalCaseId)
          })
        }}
      />
    </Box>
  )
}

export default forwardRef(Scans)
